import React, { useMemo, useState } from 'react';
import { authToken } from '../../authToken';
import TimePicker from '../../components/TimePicker';
import { API_BASE_URL } from '../../config';
import ModalShell from '../../components/ModalShell';
import MacroCalcPanel from './MacroCalcPanel';
import FoodIntakeTemplatePicker from './FoodIntakeTemplatePicker';

const ENDPOINT_HOST = API_BASE_URL;
const ENDPOINT_FOOD = `${ENDPOINT_HOST}/foodintake`;

function toIntOrNull(s) {
  if (s === '' || s === null || s === undefined) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

function kcalFromMacros({ carbs, protein, fat }) {
  const c = Number(carbs);
  const p = Number(protein);
  const f = Number(fat);
  if (!Number.isFinite(c) || !Number.isFinite(p) || !Number.isFinite(f)) return null;
  return (4 * c) + (4 * p) + (9 * f);
}

function stripTrailingParenSuffix(s) {
  // If string ends with " (something)", strip that suffix.
  // Important: only strips the last parenthesized group at the end.
  const src = String(s ?? '');
  return src.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

function fmtOneDecimal(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return null;
  return x.toFixed(1);
}

function fmtRatioForNameSuffix(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return null;
  const isInt = Math.abs(x - Math.round(x)) < 1e-9;
  return isInt ? String(Math.round(x)) : x.toFixed(2);
}

function formatActualOverDefault({ actualPortion, defaultPortion }) {
  const a = Number(actualPortion);
  const d = Number(defaultPortion);
  if (!Number.isFinite(a)) return null;
  // If default portion is missing/invalid/zero, fall back to showing actual portion + unit name (as requested)
  if (!Number.isFinite(d) || d === 0) return null;
  const ratioStr = fmtRatioForNameSuffix(a / d);
  if (!ratioStr) return null;
  return `${ratioStr}pcs`;
}

function formatActualPortionWithUnit({ actualPortion, unitName }) {
  const a = Number(actualPortion);
  if (!Number.isFinite(a)) return null;
  const isInt = Math.abs(a - Math.round(a)) < 1e-9;
  const portionStr = isInt ? String(Math.round(a)) : a.toFixed(2);
  const unit = String(unitName ?? '').trim();
  return unit ? `${portionStr}${unit}` : portionStr;
}

export default function FoodIntakeAddPanel({ onClose = null }) {
  const [selectedTimestamp, setSelectedTimestamp] = useState(Date.now());
  const [name, setName] = useState('');
  const [carbs, setCarbs] = useState('');
  const [sugarInCarbs, setSugarInCarbs] = useState('');
  const [protein, setProtein] = useState('');
  const [fat, setFat] = useState('');
  const [kcal, setKcal] = useState('');

  // Template meta (kept separately from FoodIntake DTO)
  const [tplDefaultPortion, setTplDefaultPortion] = useState(null);
  const [tplRefUnits, setTplRefUnits] = useState(null);
  const [tplUnitName, setTplUnitName] = useState(null);
  const [actualPortion, setActualPortion] = useState(null);

  const [showMacroCalc, setShowMacroCalc] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const countedKcal = useMemo(() => {
    const c = toIntOrNull(carbs);
    const p = toIntOrNull(protein);
    const f = toIntOrNull(fat);
    if (c === null || p === null || f === null) return null;
    return kcalFromMacros({ carbs: c, protein: p, fat: f });
  }, [carbs, protein, fat]);

  const canSubmit = useMemo(() => {
    const nm = (name || '').trim();
    return (
      nm.length > 0 &&
      toIntOrNull(carbs) !== null &&
      (sugarInCarbs === '' || toIntOrNull(sugarInCarbs) !== null) &&
      toIntOrNull(protein) !== null &&
      toIntOrNull(fat) !== null &&
      toIntOrNull(kcal) !== null
    );
  }, [name, carbs, sugarInCarbs, protein, fat, kcal]);

  const handleCancel = () => {
    setSelectedTimestamp(Date.now());
    setName('');
    setCarbs('');
    setSugarInCarbs('');
    setProtein('');
    setFat('');
    setKcal('');
    setTplDefaultPortion(null);
    setTplRefUnits(null);
    setTplUnitName(null);
    setActualPortion(null);
    setError(null);
    if (onClose) onClose();
  };

  const handleOk = async () => {
    setError(null);

    const nm = (name || '').trim();
    if (!nm) {
      setError('Name is required.');
      return;
    }

    const carbsNum = toIntOrNull(carbs);
    const sugarInCarbsNum = sugarInCarbs === '' ? 0 : toIntOrNull(sugarInCarbs);
    const proteinNum = toIntOrNull(protein);
    const fatNum = toIntOrNull(fat);
    const kcalNum = toIntOrNull(kcal);

    if (carbsNum === null || sugarInCarbsNum === null || proteinNum === null || fatNum === null || kcalNum === null) {
      setError('Carbs / sugar-in-carbs / protein / fat / kcal must be integers.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(ENDPOINT_FOOD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': authToken } : {})
        },
        body: JSON.stringify({
          name: nm,
          carbs: carbsNum,
          sugarInCarbs: sugarInCarbsNum,
          protein: proteinNum,
          fat: fatNum,
          kcal: kcalNum,
          timestamp: selectedTimestamp,
        }),
      });

      if (response.ok) {
        if (onClose) onClose();
      } else {
        const errorData = await response.text();
        setError(`Failed to add food intake: ${errorData} (${response.status})`);
      }
    } catch (e) {
      console.error('Failed to add food intake:', e);
      setError('Failed to add food intake. Please check your connection.');
    } finally {
      setTimeout(() => setIsSubmitting(false), 1000);
    }
  };

  const inputStyle = {
    width: '100%',
    fontSize: '1.05rem',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ccc',
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '100px'
  };

  const smallButtonStyle = {
    padding: '10px 18px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '110px'
  };

  const formatMaybe = (v) => {
    if (v === null || v === undefined || !Number.isFinite(v)) return '—';
    return String(Math.round(v));
  };

  const openMacroCalc = () => {
    setShowMacroCalc(true);
  };

  const closeMacroCalc = () => {
    setShowMacroCalc(false);
  };

  const handleMacroCalcOk = ({ carbs, sugar, protein, fat, kcal, referenceUnits, targetUnits, unitName }) => {
    if (carbs !== null && carbs !== undefined) setCarbs(String(Math.round(carbs)));
    if (sugar !== null && sugar !== undefined) setSugarInCarbs(String(Math.round(sugar)));
    if (protein !== null && protein !== undefined) setProtein(String(Math.round(protein)));
    if (fat !== null && fat !== undefined) setFat(String(Math.round(fat)));
    if (kcal !== null && kcal !== undefined) setKcal(String(Math.round(kcal)));

    // Keep template meta in sync with calc settings (if provided)
    const nextRefUnits = (referenceUnits !== null && referenceUnits !== undefined) ? Number(referenceUnits) : tplRefUnits;
  const nextActualPortion = (targetUnits !== null && targetUnits !== undefined) ? Number(targetUnits) : actualPortion;
    const nextUnitName = (unitName !== null && unitName !== undefined) ? String(unitName) : tplUnitName;

    if (referenceUnits !== null && referenceUnits !== undefined) setTplRefUnits(nextRefUnits);
  if (targetUnits !== null && targetUnits !== undefined) setActualPortion(nextActualPortion);
    if (unitName !== null && unitName !== undefined) setTplUnitName(nextUnitName);

    // Update name suffix after calc (ratio + unit)
    setName((prev) => {
      const base = stripTrailingParenSuffix(prev);
      const suffix = (
        (tplDefaultPortion === 0 || tplDefaultPortion === null || tplDefaultPortion === undefined)
          ? formatActualPortionWithUnit({ actualPortion: nextActualPortion, unitName: nextUnitName })
          : formatActualOverDefault({ actualPortion: nextActualPortion, defaultPortion: tplDefaultPortion })
      );
      if (!suffix) return base;
      return `${base} (${suffix})`.trim();
    });

    setShowMacroCalc(false);
  };

  const openTemplatePicker = () => {
    setShowTemplatePicker(true);
  };

  const closeTemplatePicker = () => {
    setShowTemplatePicker(false);
  };

  const handleTemplatePicked = (tpl) => {
    // Map template DTO -> FoodIntake DTO
    // - template.carbsWithSugar -> foodintake.carbs
    // - template.sugars -> foodintake.sugarInCarbs
    // - copy remaining macros + kcal
    const br = String(tpl?.brand ?? '').trim();
    const nm = String(tpl?.name ?? '').trim();
    const baseName = nm ? (br ? `[${br}] ${nm}` : nm) : '';
    if (tpl?.carbsWithSugar !== null && tpl?.carbsWithSugar !== undefined) setCarbs(String(tpl.carbsWithSugar));
    if (tpl?.sugars !== null && tpl?.sugars !== undefined) setSugarInCarbs(String(tpl.sugars));
    if (tpl?.protein !== null && tpl?.protein !== undefined) setProtein(String(tpl.protein));
    if (tpl?.fat !== null && tpl?.fat !== undefined) setFat(String(tpl.fat));
    if (tpl?.kcal !== null && tpl?.kcal !== undefined) setKcal(String(tpl.kcal));

    // Remember template meta for later MacroCalc usage
    const nextPortion = (tpl?.defaultPortion !== null && tpl?.defaultPortion !== undefined) ? Number(tpl.defaultPortion) : null;
    const nextRefUnits = (tpl?.refUnits !== null && tpl?.refUnits !== undefined) ? Number(tpl.refUnits) : null;
    const nextUnitName = (tpl?.unitName !== null && tpl?.unitName !== undefined) ? String(tpl.unitName) : null;

    setTplDefaultPortion(nextPortion);
    setTplRefUnits(nextRefUnits);
    setTplUnitName(nextUnitName);
  setActualPortion(nextPortion);

    // Update name suffix after load
    setName(() => {
      const base = stripTrailingParenSuffix(baseName);
      const suffix = (
        nextPortion === 0
          ? formatActualPortionWithUnit({ actualPortion: nextPortion, unitName: nextUnitName })
          : formatActualOverDefault({ actualPortion: nextPortion, defaultPortion: nextPortion })
      );
      if (!suffix) return base;
      return `${base} (${suffix})`.trim();
    });

    setShowTemplatePicker(false);
  };

  const templateMetaLine = useMemo(() => {
    if (tplDefaultPortion === null && actualPortion === null && tplRefUnits === null && !tplUnitName) return null;
    const portion = actualPortion === null ? '—' : String(Math.round(actualPortion));
    const def = tplDefaultPortion === null ? '—' : String(Math.round(tplDefaultPortion));
    const ref = tplRefUnits === null ? '—' : String(Math.round(tplRefUnits));
    const unit = String(tplUnitName || '').trim() || '—';
    return `tpl: portion ${portion}/${def} ${unit} • ref ${ref} ${unit}`;
  }, [actualPortion, tplDefaultPortion, tplRefUnits, tplUnitName]);

  return (
    <div style={{
      padding: '10px 0',
      maxWidth: '420px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start'
    }}>
      <div>
        <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>🍽️ Add Food Intake</h3>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            textAlign: 'center',
            border: '1px solid #f5c6cb'
          }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
            autoFocus
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input type="number" placeholder="Carbs (g)" value={carbs} onChange={(e) => setCarbs(e.target.value)} style={inputStyle} />
            <input type="number" placeholder="Sugar in carbs (g)" value={sugarInCarbs} onChange={(e) => setSugarInCarbs(e.target.value)} style={inputStyle} />
            <input type="number" placeholder="Protein (g)" value={protein} onChange={(e) => setProtein(e.target.value)} style={inputStyle} />
            <input type="number" placeholder="Fat (g)" value={fat} onChange={(e) => setFat(e.target.value)} style={inputStyle} />
            <input type="number" placeholder="kcal" value={kcal} onChange={(e) => setKcal(e.target.value)} style={inputStyle} />
            <div
              style={{
                ...inputStyle,
                borderStyle: 'dashed',
                borderColor: '#ff8a00',
                background: '#fff',
                fontWeight: 800,
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="calced kcal"
              title="calced kcal from macros (4*C + 4*P + 9*F); sugars assumed included in carbs"
            >
              calced kcal: {formatMaybe(countedKcal)}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: '10px' }}>
          <button
            type="button"
            onClick={openTemplatePicker}
            style={{
              ...smallButtonStyle,
              backgroundColor: '#6c757d',
              color: 'white',
            }}
          >
            Load
          </button>
          <button
            type="button"
            onClick={openMacroCalc}
            style={{
              ...smallButtonStyle,
              backgroundColor: '#ff8a00',
              color: 'white',
            }}
          >
            Calc
          </button>
        </div>

        {templateMetaLine && (
          <div style={{
            textAlign: 'center',
            margin: '-2px 0 10px 0',
            fontSize: 12,
            fontWeight: 800,
            color: '#666',
            userSelect: 'none'
          }}>
            {templateMetaLine}
          </div>
        )}

         <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
          <TimePicker initialTimestamp={selectedTimestamp} onTimeChange={setSelectedTimestamp} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: 12 }}>
        <button
          onClick={handleCancel}
          disabled={isSubmitting}
          style={{
            ...buttonStyle,
            backgroundColor: '#6c757d',
            color: 'white',
            opacity: isSubmitting ? 0.6 : 1
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleOk}
          disabled={isSubmitting || !canSubmit}
          style={{
            ...buttonStyle,
            backgroundColor: '#ff8a00',
            color: 'white',
            opacity: (isSubmitting || !canSubmit) ? 0.6 : 1
          }}
        >
          {isSubmitting ? 'Adding...' : 'OK'}
        </button>
      </div>

      {showMacroCalc && ( 
        <ModalShell width={420} height={560}>
          <MacroCalcPanel
            initialValues={{
              sugar: toIntOrNull(sugarInCarbs) ?? 0,
              carbs: toIntOrNull(carbs) ?? 0,
              protein: toIntOrNull(protein) ?? 0,
              fat: toIntOrNull(fat) ?? 0,
              kcal: toIntOrNull(kcal) ?? 0,
              referenceUnits: (tplRefUnits ?? 100),
              targetUnits: (actualPortion ?? tplDefaultPortion ?? 100),
              unitName: (tplUnitName ?? ''),
            }}
            onOk={handleMacroCalcOk}
            onClose={closeMacroCalc}
          />
        </ModalShell>
      )}

      {showTemplatePicker && (
        <ModalShell width={520} height={520}>
          <FoodIntakeTemplatePicker
            onOk={handleTemplatePicked}
            onCancel={closeTemplatePicker}
          />
        </ModalShell>
      )}
    </div>
  );
}
